import React, { Fragment, useCallback, useState } from "react";
import { ConfigurationActions, RootDispatch, useTypedSelector } from "../redux";
import { Link, useNavigate } from "react-router-dom";
import { InitializerIndicator } from "../components/InitializerIndicator";
import { Button } from "../components/core/Button";
import {
    Grid,
    Box,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Divider,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Menu,
    MenuItem,
    MenuProps,
    Typography,
    useMediaQuery,
    Tooltip,
} from "@material-ui/core";
import { ChangeTypes, ChangeNames, unwrap, DocumentState } from "../clients/FirebaseClient/types";
import { Colors } from "../styles";

import { makeStyles, useTheme, withStyles } from "@material-ui/core/styles";
import { useDispatch } from "react-redux";
import { useStyles } from "../useStyles";
import OverviewDeleteIcon from "../assets/images/overview_delete_icon.svg";
import OverviewEditIcon from "../assets/images/overview_edit_icon.svg";
import OverviewDownloadIcon from "../assets/images/overview_download_icon.svg";
import OverviewActionIcon from "../assets/images/overview_action_icon.svg";
import OverviewSummaryIcon from "../assets/images/overview_summary_icon.svg";
import CloseIcon from "../assets/images/close_icon.svg";
import DownloadSvgIcon from "../assets/images/download_icon.svg";
import { collectAllFilesArraysFromConfiguration } from "./summary";
import moment from "moment";
import { defaultFirebaseClient } from "../clients/FirebaseClient";
import { formatFileSize } from "../utils/filetypes";

const useStyles2 = makeStyles({
    container: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
    },
    power: {
        height: 20,
        width: 53,
        borderRadius: 24,
        backgroundColor: Colors.pulsingPurple,
        fontFamily: "innogyWEB",
        fontSize: 14,
        fontWeight: 500,
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: "normal",
        letterSpacing: "normal",
        color: Colors.white,
        textAlign: "center",
        marginRight: 7,
        verticalAlign: "middle",
    },
    gas: {
        height: 20,
        width: 39,
        borderRadius: 24,
        backgroundColor: Colors.pulsingPurple,
        fontFamily: "innogyWEB",
        fontSize: 14,
        fontWeight: 500,
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: "normal",
        letterSpacing: "normal",
        color: Colors.white,
        textAlign: "center",
        verticalAlign: "middle",
    },
    link: {
        color: "inherit",
        textDecoration: "inherit",
    },
    h2: {
        fontFamily: "innogyWEB",
        fontSize: 37,
        fontWeight: 500,
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: "normal",
        letterSpacing: "normal",
        color: Colors.anthracite,
    },
    tableCell: {
        fontFamily: "innogyWEB",
        fontSize: 22,
        fontWeight: 500,
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: 1.45,
        letterSpacing: "normal",
        color: Colors.anthracite,
    },
    tableCellContent: {
        fontFamily: "innogyWEB",
        fontSize: 22,
        fontWeight: 300,
        fontStretch: "normal",
        fontStyle: "normal",
        lineHeight: 1.94,
        letterSpacing: "normal",
        color: Colors.anthracite,
    },
    verticalTableTitel: {
        fontFamily: "innogyWEB",
        fontSize: 18,
        fontStretch: "normal",
        fontStyle: "normal",
        letterSpacing: "normal",
        lineHeight: 1.4,
        width: "100%",
        fontWeight: 500,
        color: Colors.anthracite,
    },
    verticalTableText: {
        fontFamily: "innogyWEB",
        fontSize: 18,
        fontStretch: "normal",
        fontStyle: "normal",
        letterSpacing: "normal",
        lineHeight: 1.4,
        width: "100%",
        fontWeight: 300,
        color: Colors.anthracite,
    },
    icon: {
        width: "24px",
        height: "24px",
        margin: "0 8px",
    },
    dialogTitle: {
        fontFamily: "innogyWEB",
        fontSize: 24,
        fontStretch: "normal",
        fontStyle: "normal",
        letterSpacing: "normal",
        lineHeight: 1.25,
        fontWeight: 500,
        color: Colors.anthracite,
    },
});

const formatAddress = (address: ChangeTypes[ChangeNames.BUILDING_ADDRESS]): string => {
    const { street, houseNumber, city, zipCode, district } = unwrap(address);

    if (address.type === "postal") {
        return `${street} ${houseNumber}, ${zipCode} ${city}${district ? ` (${district})` : ""}`;
    } else {
        const placePart = `${zipCode} ${city}${district ? ` (${district})` : ""}`;
        const { mark, corridor, parcel } = unwrap(address);
        const nonStreetPart = `${mark ? `${mark}` : ""}${corridor ? ` ${corridor}` : ""}${
            parcel ? ` ${parcel}` : ""
        }`;
        const optionalStreetPart = street
            ? `${street}${houseNumber ? ` ${houseNumber}` : ""}, `
            : "";
        return `[${nonStreetPart}] ${optionalStreetPart}${placePart}`;
    }
};

export const Overview = (): JSX.Element => {
    const classes2 = useStyles2();

    const orders = useTypedSelector((state) => state.orders);
    const configurations = useTypedSelector((state) =>
        Object.values(state.configuration.configurations),
    );

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const [currentConfId, setCurrentConfId] = useState<string>();
    const [isActionsOpen, setIsActionsOpen] = useState<boolean>(false);

    const crmId = useCallback(
        (configId: string | undefined) =>
            orders.find((order) => order.configurationId === configId)?.crm?.id || "processing",
        [orders],
    );

    const openActions = (event: React.MouseEvent<HTMLButtonElement>, confId: string) => {
        setAnchorEl(event.currentTarget);
        setCurrentConfId(confId);
        setIsActionsOpen(true);
    };

    const actionCell = useCallback(
        (confId: string) => {
            return (
                <TableCell style={{ paddingRight: 0 }}>
                    <div
                        style={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "flex-end",
                        }}
                    >
                        <IconButton
                            onClick={(event) => openActions(event, confId)}
                            style={{ padding: 8 }}
                        >
                            <img
                                src={OverviewActionIcon}
                                alt="Aktionen"
                                width="24px"
                                height="24px"
                            />
                        </IconButton>

                        {currentConfId && (
                            <OverviewActions
                                open={isActionsOpen}
                                onClose={() => {
                                    setIsActionsOpen(false);
                                }}
                                onRemoveCurrentConfId={() => setCurrentConfId(undefined)}
                                anchorEl={anchorEl}
                                confId={currentConfId}
                            />
                        )}
                    </div>
                </TableCell>
            );
        },
        [anchorEl, currentConfId, isActionsOpen],
    );

    return (
        <InitializerIndicator>
            <Box p={4} data-testid="overview-testId">
                <Grid container style={{ flexDirection: "column" }} spacing={3}>
                    <Grid container style={{ justifyContent: "space-between" }}>
                        <Grid item>
                            <h2 className={classes2.h2} style={{ marginLeft: 10 }}>
                                Meine Anträge
                            </h2>
                        </Grid>
                    </Grid>

                    <Grid item>
                        <Box sx={{ display: { xs: "none", sm: "block" } }}>
                            <Table
                                size="small"
                                style={{
                                    width: "100%",
                                }}
                            >
                                <TableHead>
                                    <TableRow>
                                        <TableCell
                                            className={classes2.tableCell}
                                            style={{ paddingLeft: 0 }}
                                        >
                                            Vorgangsnummer
                                        </TableCell>
                                        <TableCell className={classes2.tableCell}>
                                            Bauadresse
                                        </TableCell>
                                        <TableCell className={classes2.tableCell}>
                                            Anschlussart
                                        </TableCell>

                                        <TableCell className={classes2.tableCell}>
                                            Maßnahme
                                        </TableCell>
                                        <TableCell></TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {configurations
                                        .filter((it) => it.id !== "new" && it.lockedAt)
                                        .map((conf) => {
                                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                            const productClasses =
                                                conf.state.productClasses?.state.value!;
                                            const productTypes =
                                                conf.state.productTypes?.state.value;
                                            const buildingAddress = formatAddress(
                                                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                                conf.state.buildingAddress?.state!,
                                            );
                                            const isLocked = conf.lockedAt;
                                            return (
                                                <TableRow
                                                    key={conf.id}
                                                    style={{
                                                        backgroundColor: "white",
                                                    }}
                                                >
                                                    <TableCell
                                                        style={{ paddingLeft: 0 }}
                                                        className={classes2.tableCellContent}
                                                    >
                                                        <Link
                                                            to={`/configuration/${conf.id}`}
                                                            className={classes2.link}
                                                        >
                                                            {isLocked && crmId(conf.id)}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell
                                                        className={classes2.tableCellContent}
                                                    >
                                                        <Link
                                                            to={`/configuration/${conf.id}`}
                                                            className={classes2.link}
                                                        >
                                                            {buildingAddress}
                                                        </Link>
                                                    </TableCell>
                                                    <TableCell>
                                                        <Link
                                                            to={`/configuration/${conf.id}`}
                                                            className={classes2.link}
                                                        >
                                                            <div className={classes2.container}>
                                                                {(productTypes?.charger ||
                                                                    productTypes?.adjustableLoad ||
                                                                    productClasses.power) && (
                                                                    <p className={classes2.power}>
                                                                        Strom
                                                                    </p>
                                                                )}
                                                                {productClasses?.gas && (
                                                                    <p className={classes2.gas}>
                                                                        Gas
                                                                    </p>
                                                                )}
                                                            </div>
                                                        </Link>
                                                    </TableCell>

                                                    <TableCell
                                                        className={classes2.tableCellContent}
                                                    >
                                                        <Link
                                                            to={`/configuration/${conf.id}`}
                                                            className={classes2.link}
                                                        >
                                                            {productTypes?.newConnection &&
                                                                "Neuanschluss"}
                                                            {productTypes?.modification &&
                                                                "Änderung"}
                                                            {productTypes?.removal && "Demontage"}
                                                            {productTypes?.charger &&
                                                                "Ladeeinrichtung"}
                                                            {productTypes?.increase &&
                                                                "Leistungserhöhung"}
                                                            {productTypes?.adjustableLoad &&
                                                                "Verbrauchsgeräte"}
                                                        </Link>
                                                    </TableCell>

                                                    {conf.id && actionCell(conf.id)}
                                                </TableRow>
                                            );
                                        })}
                                </TableBody>
                            </Table>
                        </Box>
                    </Grid>
                    <Box sx={{ display: { xs: "block", sm: "none" } }}>
                        <Divider style={{ marginTop: 20, marginBottom: 20, maxWidth: 405 }} />
                        {configurations
                            .filter((it) => it.id !== "new" && it.lockedAt)
                            .map((conf) => {
                                const productClasses = conf.state.productClasses?.state.value;
                                const productTypes = conf.state.productTypes?.state.value;
                                const buildingAddress = formatAddress(
                                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                                    conf.state.buildingAddress?.state!,
                                );
                                const isLocked = conf.lockedAt;
                                return (
                                    <Table key={conf.id}>
                                        <TableBody>
                                            <TableRow
                                                style={{
                                                    backgroundColor: "white",
                                                }}
                                            >
                                                <TableCell>
                                                    <div className={classes2.verticalTableTitel}>
                                                        Vorgangs-Nr.
                                                    </div>
                                                    <div
                                                        style={{ paddingLeft: 0 }}
                                                        className={classes2.verticalTableText}
                                                    >
                                                        {isLocked && crmId(conf.id)}
                                                    </div>
                                                    <div
                                                        className={classes2.verticalTableTitel}
                                                        style={{ marginTop: 17 }}
                                                    >
                                                        Bauadresse
                                                    </div>
                                                    <div className={classes2.verticalTableText}>
                                                        {buildingAddress}
                                                    </div>

                                                    <div
                                                        className={classes2.verticalTableTitel}
                                                        style={{ marginTop: 17 }}
                                                    >
                                                        Anschlussart
                                                    </div>
                                                    <div>
                                                        <div className={classes2.container}>
                                                            {(productTypes?.charger ||
                                                                productClasses?.power) && (
                                                                <p className={classes2.power}>
                                                                    Strom
                                                                </p>
                                                            )}
                                                            {productClasses?.gas && (
                                                                <p className={classes2.gas}>Gas</p>
                                                            )}
                                                        </div>
                                                    </div>
                                                    <div className={classes2.verticalTableTitel}>
                                                        Maßnahme
                                                    </div>
                                                    <div className={classes2.verticalTableText}>
                                                        {productTypes?.newConnection &&
                                                            "Neuanschluss"}
                                                        {productTypes?.modification && "Änderung"}
                                                        {productTypes?.removal && "Demontage"}
                                                    </div>
                                                </TableCell>
                                                {conf.id && actionCell(conf.id)}
                                            </TableRow>
                                        </TableBody>
                                    </Table>
                                );
                            })}
                        <div style={{ marginBottom: 20 }}> </div>
                    </Box>
                </Grid>
            </Box>
            <Typography
                style={{
                    fontFamily: "innogyWEB",
                    fontSize: "16px",
                    fontWeight: 300,
                    padding: "0 32px 32px 32px",
                }}
            >
                Ihr Antrag wird gemäß DSGVO automatisch 36 Monate nach dem Absenden gelöscht. Sie
                haben die Möglichkeit, den Antrag für Ihre Unterlagen herunterzuladen und vor Ablauf
                der Frist manuell zu löschen. Nicht abgesendete Anträge (ohne Vorgangsnummer) werden
                automatisch nach 6 Monaten verworfen.
            </Typography>
        </InitializerIndicator>
    );
};

const StyledMenu = withStyles({
    paper: {
        width: "220px",
        borderRadius: "4px",
        boxShadow: "0 2px 8px 0 #00000033",
    },
    list: {
        padding: "0px",
    },
})((props: MenuProps) => (
    <Menu
        anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
        }}
        transformOrigin={{
            vertical: "top",
            horizontal: "right",
        }}
        getContentAnchorEl={null}
        {...props}
    />
));

const StyledMenuItem = withStyles({
    root: {
        borderTop: `1px solid ${Colors.paleTwo}`,
        color: Colors.anthracite,
        fontSize: "16px",
        padding: "12px 12px 12px 0",
    },
})(MenuItem);

const OverviewActions = (props: {
    open: boolean;
    onClose: () => void;
    onRemoveCurrentConfId: () => void;
    anchorEl: HTMLElement | null;
    confId: string;
}) => {
    const classes = useStyles();
    const classes2 = useStyles2();

    const navigate = useNavigate();
    const configuration = useTypedSelector(
        (state) => state.configuration.configurations[props.confId],
    );

    const order = useTypedSelector((state) =>
        state.orders.find((order) => order.configurationId === props.confId),
    );

    const isNotLocked = !configuration.lockedAt;

    const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
    const [downloadDialogOpen, setDownloadDialogOpen] = useState<boolean>(false);

    return (
        <Fragment>
            <StyledMenu anchorEl={props.anchorEl} open={props.open} onClose={props.onClose}>
                <Typography
                    className={classes2.verticalTableTitel}
                    style={{ padding: "16px 16px 4px 16px" }}
                >
                    Aktionen
                </Typography>
                {isNotLocked ? (
                    <Box height="12px" />
                ) : (
                    <Typography
                        className={classes2.verticalTableTitel}
                        style={{
                            fontSize: 14,
                            color: Colors.pumpkin,
                            padding: "0px 16px 16px 16px",
                        }}
                    >
                        Antrag wird nach dem{" "}
                        {moment.unix(configuration.lockedAt!).add(36, "months").format("DD.MM.YY")}{" "}
                        automatisch gelöscht.
                    </Typography>
                )}

                {isNotLocked
                    ? [
                          <StyledMenuItem
                              key={`edit${props.confId}`}
                              className={classes.pSmall}
                              onClick={() => {
                                  props.onClose();
                                  navigate(`/configuration/${props.confId}`);
                              }}
                          >
                              <img
                                  src={OverviewEditIcon}
                                  alt="Bearbeiten"
                                  className={classes2.icon}
                              />
                              Antrag bearbeiten
                          </StyledMenuItem>,
                      ]
                    : [
                          <StyledMenuItem
                              key={`download${props.confId}`}
                              className={classes.pSmall}
                              onClick={() => {
                                  props.onClose();
                                  setDownloadDialogOpen(true);
                              }}
                          >
                              <img
                                  src={OverviewDownloadIcon}
                                  alt="Herunterladen"
                                  className={classes2.icon}
                              />
                              Antrag herunterladen
                          </StyledMenuItem>,
                          <StyledMenuItem
                              key={`summary${props.confId}`}
                              className={classes.pSmall}
                              onClick={() => {
                                  props.onClose();
                                  navigate(`/configuration/${props.confId}`);
                              }}
                          >
                              <img
                                  src={OverviewSummaryIcon}
                                  alt="Anzeigen"
                                  style={{
                                      width: "24px",
                                      height: "22px",
                                      margin: "0 8px",
                                  }}
                              />
                              Antrag anzeigen
                          </StyledMenuItem>,
                      ]}
                <Tooltip
                    title={
                        (order &&
                            !order.crm &&
                            "Kann nicht während der Verarbeitung gelöscht werden.") ||
                        ""
                    }
                >
                    <Box>
                        <StyledMenuItem
                            key={`delete${props.confId}`}
                            className={classes.pSmall}
                            onClick={async () => {
                                setDeleteDialogOpen(true);
                                props.onClose();
                            }}
                            disabled={order && !order.crm}
                        >
                            <img src={OverviewDeleteIcon} alt="Löschen" className={classes2.icon} />
                            Antrag löschen
                        </StyledMenuItem>
                    </Box>
                </Tooltip>
            </StyledMenu>
            <DeleteDialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                confId={props.confId}
                onRemoveCurrentConfId={props.onRemoveCurrentConfId}
            />
            <DownloadDialog
                open={downloadDialogOpen}
                onClose={() => setDownloadDialogOpen(false)}
                confId={props.confId}
            />
        </Fragment>
    );
};

const DeleteDialog = (props: {
    open: boolean;
    onClose: () => void;
    confId: string;
    onRemoveCurrentConfId: () => void;
}) => {
    const classes2 = useStyles2();
    const dispatch: RootDispatch = useDispatch();

    const objectToRemove = useTypedSelector((state) => {
        const order = state.orders.find((order) => order.configurationId === props.confId);
        if (order) {
            return { orderId: order.id };
        } else {
            return { confId: props.confId };
        }
    });

    return (
        <Dialog open={props.open} onClose={props.onClose}>
            <DialogTitle>
                <div className={classes2.h2}>Antrag löschen</div>
            </DialogTitle>
            <DialogContent>
                <div className={classes2.tableCellContent}>
                    Möchten Sie diesen Antrag unwiderruflich löschen?
                </div>
            </DialogContent>
            <DialogActions style={{ padding: 16 }}>
                <Button mode="secondary" onClick={props.onClose}>
                    Nein
                </Button>
                <div style={{ flex: 1 }} />
                <Button
                    mode="primary"
                    onClick={() => {
                        dispatch(ConfigurationActions.remove(objectToRemove));
                        props.onClose();
                        props.onRemoveCurrentConfId();
                    }}
                >
                    Ja
                </Button>
            </DialogActions>
        </Dialog>
    );
};

const DownloadDialog = (props: { open: boolean; onClose: () => void; confId: string }) => {
    const classes = useStyles();
    const classes2 = useStyles2();

    const configuration = useTypedSelector(
        (state) => state.configuration.configurations[props.confId],
    );

    const allFilesArrays = collectAllFilesArraysFromConfiguration(configuration);
    const allFiles = (allFilesArrays as [])
        .reduce((acc, e) => acc.concat(e), [])
        .map((file) => file as DocumentState["files"][0])
        .sort((fileA, fileB) => fileB.createdAt - fileA.createdAt);

    const summaryObjectId = useTypedSelector((state) => {
        const order = state.orders.find((order) => order.configurationId === configuration.id);
        return order?.summary?.objectId;
    });
    return (
        <Dialog open={props.open} onClose={props.onClose} maxWidth="md" fullWidth>
            <DialogTitle
                disableTypography
                style={{
                    display: "flex",
                    flexDirection: "row",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "20px 24px 20px 32px",
                }}
            >
                <Typography className={classes2.dialogTitle}>Antrag herunterladen</Typography>
                <IconButton onClick={props.onClose} style={{ padding: 8 }}>
                    <img src={CloseIcon} alt="Schließen" width="24px" height="24px" />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers style={{ padding: "40px" }}>
                <Box
                    display="flex"
                    flexDirection="column"
                    padding="24px"
                    bgcolor={Colors.anthracite5}
                    borderRadius="8px"
                >
                    <Typography className={classes.h4} style={{ fontSize: 20 }}>
                        Ihre Dokumente
                    </Typography>
                    <Divider style={{ margin: "16px 0", backgroundColor: Colors.paleTwo }} />
                    {!summaryObjectId && allFiles.length === 0 && (
                        <Typography
                            className={classes.p}
                            style={{
                                fontSize: 20,
                                fontWeight: 300,
                            }}
                        >
                            Dokumente werden vorbereitet
                        </Typography>
                    )}
                    {summaryObjectId && (
                        <Document
                            title="Zusammenfassung (PDF)"
                            timeStamp={moment
                                .unix(configuration.lockedAt!)
                                .format("DD.MM.Y, HH:mm U\\hr")
                                .toString()}
                            objectId={summaryObjectId}
                        />
                    )}
                    {allFiles.map((file, idx) => {
                        const key = moment.unix(file.createdAt).toString();
                        return [
                            summaryObjectId || (!summaryObjectId && idx !== 0) ? (
                                <Box key={`${key}_margin_${idx}`} mt="16px" />
                            ) : null,
                            <Document
                                key={key}
                                title={`${file.userFilename} (${file.extension.toUpperCase()},${" "}${formatFileSize(file.size)})`}
                                timeStamp={moment
                                    .unix(file.createdAt)
                                    .format("DD.MM.Y, HH:mm U\\hr")
                                    .toString()}
                                objectId={file.objectId}
                            />,
                        ];
                    })}
                </Box>
            </DialogContent>
        </Dialog>
    );
};

const Document = (props: { title: string; timeStamp: string; objectId: string }) => {
    const classes = useStyles();
    const theme = useTheme();
    const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
    return (
        <Box
            display="flex"
            alignItems="center"
            onClick={async () =>
                (window.location.href = await defaultFirebaseClient.getDownloadUrl(props.objectId))
            }
            style={{ cursor: "pointer" }}
        >
            <img alt="Download" width="31px" height="31px" src={DownloadSvgIcon} />
            <Box mr="16px" />
            <Box
                display="flex"
                alignItems={isSmall ? "normal" : "center"}
                flexDirection={isSmall ? "column" : "row"}
                justifyContent="space-between"
                width="100%"
            >
                <Typography
                    className={classes.p}
                    style={{
                        fontSize: 20,
                        fontWeight: 300,
                    }}
                >
                    {props.title}
                </Typography>
                <Typography
                    className={classes.p}
                    style={{
                        fontSize: 20,
                        color: Colors.anthracite50,
                        fontWeight: 300,
                    }}
                >
                    {props.timeStamp}
                </Typography>
            </Box>
        </Box>
    );
};
